import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../@/components/ui/select";
import AddNoteDialog from "./addNote";
import { Spinner } from "../components/spinner";
import { EmptyState } from "../components/emptyState";
import axios from "axios";
import { toast } from "sonner";
import { api_endpoint } from "../constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

export default function MarkAttendanceDialog({
  open,
  onOpenChange,
  sessionTitle = "24A Python Programming",
  sessionDate = "Wed, 24 Sep",
  attendees: initialAttendees = [],
  loading = false,
  sessionId,
  onAttendanceSaved,
}) {
  const [attendees, setAttendees] = useState(initialAttendees);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteStudentId, setNoteStudentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [markAllType, setMarkAllType] = useState(null);
  const { getToken } = useAuth();

  useEffect(() => {
    setAttendees(initialAttendees);
  }, [initialAttendees]);

  const handleSaveAttendance = async () => {
    try {
      setSaving(true);
      const token = await getToken();
      if (!token) throw new Error("No auth token found");
      const validSessionId = Number(sessionId);
      if (!validSessionId || isNaN(validSessionId)) {
        toast.error("Invalid session ID");
        setSaving(false);
        return;
      }
      const payload = {
        attendances: attendees.map(a => ({
          user_id: a.user_id,
          session_id: validSessionId,
          status: a.status,
          note: a.note,
        })),
        session_id: validSessionId,
      };
      await axios.post(
        api_endpoint + "/liveclass/mark-attendance",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Attendance saved!");
      onOpenChange(false);
      if (onAttendanceSaved) onAttendanceSaved();
    } catch (error) {
      toast.error("Failed to save attendance");
      console.error("Error saving attendance:", error);
    } finally {
      setSaving(false);
    }
  };

  const presentCount = attendees.filter(
    (a) =>
      a.status === "Present Online" ||
      a.status === "Present In-Person" ||
      a.status === "Late"
  ).length;

  const markAll = (type) => {
    setAttendees(attendees.map(a => ({
      ...a,
      status: type,
    })));
    setMarkAllType(type);
  };

  const updateStatus = (user_id, status) => {
    setAttendees(attendees.map(a => a.user_id === user_id ? { ...a, status } : a));
  };

  const addNote = (user_id, note) => {
    setAttendees(attendees.map(a => a.user_id === user_id ? { ...a, note } : a));
  };

  const noteStudent = attendees.find(a => a.user_id === noteStudentId);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="flex flex-col items-stretch w-full p-0 overflow-x-hidden border border-(--neutral-300) text-(--neutral-900)"
          onInteractOutside={(e) => {
            if (noteDialogOpen) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (noteDialogOpen) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (noteDialogOpen) e.preventDefault();
          }}
        >
          <DialogHeader className="w-full px-6 pt-6">
            <DialogTitle className="font-heading text-xl text-(--neutral-900)">Mark Attendance</DialogTitle>
            <div className="mt-1 font-body text-base text-(--neutral-600)">{sessionTitle} &bull; {sessionDate}</div>
          </DialogHeader>
          <div className="flex-1 w-full px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Spinner />
              </div>
            ) : (
              <>
                <div className="flex flex-col md:flex-row md:items-center gap-2 w-full min-w-0 mb-2">
                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 w-full min-w-0">
                    <div>
                      {presentCount === 0 ? (
                        <span className="inline-block rounded-full border border-(--color-gold) bg-(--color-gold) px-2 py-1 text-xs font-semibold text-white font-body">
                          PENDING
                        </span>
                      ) : (
                        <span className="font-heading text-sm text-(--neutral-700)">
                          {presentCount}/{attendees.length} present
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row flex-1 items-stretch sm:items-center gap-2 min-w-0">
                      <Button
                        variant="outline"
                        className={`p-2 w-full sm:w-auto whitespace-normal ${markAllType === "Present Online" ? "border-(--color-accent) text-(--color-accent)" : ""}`}
                        onClick={() => markAll("Present Online")}
                      >
                        Mark All Present Online
                      </Button>
                      <Button
                        variant="outline"
                        className={`p-2 w-full sm:w-auto whitespace-normal ${markAllType === "Present In-Person" ? "border-(--color-accent) text-(--color-accent)" : ""}`}
                        onClick={() => markAll("Present In-Person")}
                      >
                        Mark All Present In-Person
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 w-full pb-0">
                  {attendees.map((a, index) => {
                    const attendeeUserId = a.user_id ?? a.userId ?? a.id ?? "user";
                    const attendeeAttendanceId = a.attendance_id ?? a.attendanceId ?? a.id ?? "att";
                    const attendeeKey = `${sessionId ?? "session"}-${String(attendeeUserId)}-${String(attendeeAttendanceId)}-${index}`;

                    return (
                      <div key={attendeeKey} className="rounded-lg border border-(--neutral-300) px-3 py-3 shadow-xs w-full">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 w-full min-w-0">
                        {/* Attendee details */}
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-(--neutral-900)">{a.firstName} {a.lastName}</div>
                          <div className="text-sm text-(--neutral-500) truncate" title={a.email}>
                            {a.email}
                          </div>
                        </div>
                        {/* Select dropdown */}
                        <div className="flex flex-row items-center gap-2">
                          <div className="w-32 shrink-0">
                            <Select value={a.status} onValueChange={status => updateStatus(a.user_id, status)}>
                              <SelectTrigger className="w-32 rounded-md border-(--neutral-300) bg-(--surface-base) truncate">
                                <SelectValue placeholder="Not Marked" className="truncate" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Not Marked">Not Marked</SelectItem>
                                <SelectItem value="Present Online">Present Online</SelectItem>
                                <SelectItem value="Present In-Person">Present In-Person</SelectItem>
                                <SelectItem value="Late">Late</SelectItem>
                                <SelectItem value="Absent">Absent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Add Note button */}
                          <div className="w-20 shrink-0">
                            <Button
                              variant="outline"
                              className="w-full font-body text-sm"
                              onClick={() => {
                                setNoteStudentId(a.user_id);
                                setNoteDialogOpen(true);
                              }}
                            >
                              {a.note && a.note.trim() !== "" ? "Edit Note" : "Add Note"}
                            </Button>
                          </div>
                        </div>
                      </div>
                      {a.note && (
                        <div className="mt-2 rounded-md bg-(--neutral-100) px-2 py-2 text-sm text-(--neutral-700) wrap-break-word overflow-hidden">
                          <span className="font-semibold font-body text-(--neutral-800)">Note:</span> {a.note}
                        </div>
                      )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <DialogFooter className="w-full px-6 pb-4 -mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleSaveAttendance} disabled={saving}>
              {saving ? <ClipLoader size={20} color="#fff" /> : "Save Attendance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AddNoteDialog
        open={noteDialogOpen}
        onOpenChange={setNoteDialogOpen}
        studentName={noteStudent ? `${noteStudent.firstName} ${noteStudent.lastName}` : ""}
        initialNote={noteStudent?.note || ""}
        onSave={note => addNote(noteStudentId, note)}
      />
    </>
  );
}